import React, { useState } from 'react';

import { Box, Button, H1, HStack, Input, Select, Separator, Text, Tooltip, VStack } from '@betterment/samba-react';
import { useNavigate } from 'react-router-dom';

import { FlowLayout } from './FlowLayout';
import { useDistributionFlow } from './hooks';

import { US_STATES } from '../Shared/constants';

const Withholding: React.FC = () => {
  const navigate = useNavigate();
  const { nextStep } = useDistributionFlow();

  const [federalWithholding, setFederalWithholding] = useState('20.0');
  const [state, setState] = useState('');

  const handleContinue = () => {
    if (nextStep) void navigate(`../${nextStep}`, { relative: 'path' });
  };

  return (
    <FlowLayout gap="space.400">
      {/* Page title */}
      <VStack css={{ gap: 'space.200' }}>
        <H1 variant="title400">Review your distribution and set tax withholding.</H1>
        <Text variant="text100">
          Your distribution is subject to federal tax withholding and may also be subject to state withholding,
          depending on your state of residence.
        </Text>
      </VStack>

      {/* Distribution amount summary card */}
      <Box css={{ bg: 'background.neutral', borderRadius: 'medium', p: 'space.300', width: '100%' }}>
        <VStack css={{ gap: 'space.50' }}>
          <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
            <Text variant="text100">Distribution amount</Text>
            <Tooltip>
              <Tooltip.Trigger asChild={true}>
                <Tooltip.IconButton />
              </Tooltip.Trigger>
              <Tooltip.Content side="bottom">
                <Tooltip.Text>The total amount being distributed from your 401(k).</Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          </HStack>
          <Text variant="text300">$8,000.00</Text>
        </VStack>
      </Box>

      {/* Withholding form card */}
      <Box
        css={{
          borderWidth: 'thin',
          borderStyle: 'solid',
          borderColor: 'border',
          borderRadius: 'medium',
          width: '100%',
        }}
      >
        <VStack css={{ gap: 'space.300', pt: 'space.200', pb: 'space.300', px: 'space.300' }}>
          {/* Federal withholding row */}
          <HStack css={{ justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <VStack css={{ gap: 'space.100', maxWidth: '300px', width: '100%' }}>
              <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
                <Text variant="text100">Federal withholding</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true}>
                    <Tooltip.IconButton />
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom">
                    <Tooltip.Text>
                      Federal income tax withheld from your distribution. The IRS requires at least 20% for eligible
                      rollover distributions.
                    </Tooltip.Text>
                  </Tooltip.Content>
                </Tooltip>
              </HStack>
              <Input
                aria-label="Federal withholding percentage"
                type="number"
                min="0"
                max="100"
                value={federalWithholding}
                onChange={e => setFederalWithholding(e.target.value)}
                trailingSymbol="%"
              />
              <Text variant="text50" color="text.weak">
                At least 20.0% required by IRS
              </Text>
            </VStack>
            <Text variant="text100" color="text.weak">
              $1,600.00
            </Text>
          </HStack>

          <Separator />

          {/* State of residence row */}
          <HStack css={{ justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <VStack css={{ gap: 'space.100', maxWidth: '300px', width: '100%' }}>
              <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
                <Text variant="text100">State of residence</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true}>
                    <Tooltip.IconButton />
                  </Tooltip.Trigger>
                  <Tooltip.Content side="bottom">
                    <Tooltip.Text>Your state may require additional withholding.</Tooltip.Text>
                  </Tooltip.Content>
                </Tooltip>
              </HStack>
              <Select onValueChange={setState} value={state}>
                <Select.Trigger aria-label="Select state">
                  <Select.Value placeholder="Select state" />
                </Select.Trigger>
                <Select.Content>
                  {Object.keys(US_STATES)
                    .sort()
                    .map(s => (
                      <Select.Item key={s} value={s}>
                        {s}
                      </Select.Item>
                    ))}
                </Select.Content>
              </Select>
            </VStack>
            <Text variant="text100" color="text.weak">
              —
            </Text>
          </HStack>

          <Separator />

          {/* Estimated total row */}
          <HStack css={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Text variant="text100">Estimated tax withholding</Text>
            <Text variant="text100">$1,600.00</Text>
          </HStack>
        </VStack>
      </Box>

      <Button
        variant="solidPrimary"
        css={{ width: 'fit-content' }}
        disabled={!state}
        onClick={() => void handleContinue()}
      >
        Continue
      </Button>
    </FlowLayout>
  );
};

export default Withholding;
